import axios from 'axios';
import type {
  Space, Folder, DriveItem, Transfer, FileRequest,
  WorkspaceMember, StorageRequest, UsageSummary, Tag, Comment,
  FileVersion, SharedLink, LinkStats, Notification, Person, ShareGrant, SharePermission,
  BreadcrumbItem, SearchHit, PermissionRequest,
} from '../types';
import { DRIVE_API_URL } from '../api/endpoints';
import { redirectToPlatformAuth } from '../navigation/platformAuthRedirect';
import { useAppStore } from '../store/useAppStore';
import { saveRecentCache } from './recentCache';

export const driveApi = axios.create({
  baseURL: `${DRIVE_API_URL}/api/drive/v1`,
  // Sends the .softknack.com httpOnly accessToken cookie so sk-drive-api can
  // verify the platform JWT — no client-side token, matching sk-catalog's
  // cookie-only session model.
  withCredentials: true,
});

export const resolveStorageUrl = (url: string): string =>
  url.startsWith('http') ? url : `${DRIVE_API_URL}${url}`;

// x-tenant-id / x-user-id — trusted only when sk-drive-api has
// AUTH_DEV_BYPASS=true (local dev, never production). Independent of the
// JWT/session model: these are plain non-secret ids already held in the
// store (sourced from the platform's /v1/me), not a token substitute.
driveApi.interceptors.request.use((config) => {
  const { orgId, userId } = useAppStore.getState();
  if (orgId) config.headers['x-tenant-id'] = orgId;
  if (userId) config.headers['x-user-id'] = userId;
  return config;
});

/**
 * A failed drive-API call. `.message` is the server's message (what toasts
 * show); `status`, `code` and `details` are carried alongside so callers can
 * branch on *why* it failed — without them every failure looks identical.
 */
export interface DriveApiError extends Error {
  status?: number;
  code?: string;
  details?: unknown;
}

driveApi.interceptors.response.use(
  (res) => res,
  (err) => {
    const data = err?.response?.data;
    const msg: string = data?.message ?? err?.message ?? 'Request failed';
    if (err?.response?.status === 401) {
      redirectToPlatformAuth();
    }
    const wrapped: DriveApiError = Object.assign(new Error(msg), {
      ...(err?.response?.status !== undefined ? { status: err.response.status } : {}),
      ...(data?.code ? { code: data.code } : {}),
      ...(data?.details !== undefined ? { details: data.details } : {}),
    });
    return Promise.reject(wrapped);
  },
);

// ─── Spaces ───────────────────────────────────────────────────────────────────
export const fetchSpaces = () =>
  driveApi.get<{ spaces: Space[] }>('/spaces').then(r => r.data.spaces);

export const fetchMySpace = () =>
  driveApi.get<Space>('/spaces/me').then(r => r.data);

export const createSpace = (name: string) =>
  driveApi.post<Space>('/spaces', { name }).then(r => r.data);

// ─── Folders ──────────────────────────────────────────────────────────────────
export const fetchFolders = (spaceId: string, parentFolderId?: string) =>
  driveApi.get<{ folders: Folder[] }>('/folders', { params: { space_id: spaceId, parent_folder_id: parentFolderId } }).then(r => r.data.folders);

export const fetchFolderById = (id: string) =>
  driveApi.get<Folder>(`/folders/${id}`).then(r => r.data);

// Ancestor chain (space → … → this folder) for breadcrumbs, in one round trip.
export const fetchFolderPath = (id: string) =>
  driveApi.get<{ path: BreadcrumbItem[] }>(`/folders/${id}/path`).then(r => r.data.path);

export const createFolder = (spaceId: string, name: string, parentFolderId?: string) =>
  driveApi.post<Folder>('/folders', { space_id: spaceId, name, ...(parentFolderId ? { parent_folder_id: parentFolderId } : {}) }).then(r => r.data);

export const renameFolder = (id: string, name: string) =>
  driveApi.patch<Folder>(`/folders/${id}`, { name }).then(r => r.data);

export const trashFolder = (id: string) =>
  driveApi.post(`/folders/${id}/trash`).then(r => r.data);

export const deleteFolder = (id: string) =>
  driveApi.delete(`/folders/${id}`).then(r => r.data);

// ─── Items ────────────────────────────────────────────────────────────────────
export const fetchItems = (spaceId: string, folderId?: string) =>
  driveApi.get<{ items: DriveItem[] }>('/items', { params: { space_id: spaceId, ...(folderId ? { folder_id: folderId } : {}) } }).then(r => r.data.items);

export const fetchItem = (id: string) =>
  driveApi.get<DriveItem>(`/items/${id}`).then(r => r.data);

// Name search across every space the caller can read. Each hit carries the
// space + folder chain it lives in so the result is locatable.
export const searchItems = (q: string, limit = 30) =>
  driveApi
    .get<{ items: SearchHit[]; total: number }>('/items/search', { params: { q, limit } })
    .then(r => r.data);

export const fetchRecentItems = () =>
  driveApi.get<{ items: DriveItem[] }>('/items', { params: { sort: 'last_accessed_at', limit: 50 } })
    .then(r => r.data.items)
    .then(items => { saveRecentCache(items); return items; });

export const fetchStarredItems = () =>
  driveApi.get<{ items: DriveItem[] }>('/items', { params: { starred: true } }).then(r => r.data.items);

export const fetchTrashedItems = () =>
  driveApi.get<{ items: DriveItem[] }>('/items', { params: { state: 'trashed' } }).then(r => r.data.items);

export const fetchSharedItems = () =>
  driveApi.get<{ items: DriveItem[] }>('/items', { params: { shared_with_me: true } }).then(r => r.data.items);

export const updateItem = (id: string, data: Partial<Pick<DriveItem, 'name' | 'starred' | 'availability' | 'folder_id'>>) =>
  driveApi.patch<DriveItem>(`/items/${id}`, data).then(r => r.data);

export const trashItem = (id: string) =>
  driveApi.post<DriveItem>(`/items/${id}/trash`).then(r => r.data);

export const restoreItem = (id: string) =>
  driveApi.post<DriveItem>(`/items/${id}/restore`).then(r => r.data);

export const purgeItem = (id: string, reason?: string) =>
  driveApi.post(`/items/${id}/purge`, reason ? { reason } : {}).then(r => r.data);

export const starItem = (id: string, starred: boolean) =>
  driveApi.patch<DriveItem>(`/items/${id}`, { starred }).then(r => r.data);

export const moveItem = (id: string, spaceId: string, folderId?: string) =>
  driveApi.patch<DriveItem>(`/items/${id}`, { space_id: spaceId, ...(folderId ? { folder_id: folderId } : { folder_id: null }) }).then(r => r.data);

export const duplicateItem = (id: string) =>
  driveApi.post<DriveItem>(`/items/${id}/duplicate`).then(r => r.data);

export const attachTag = (itemId: string, tagId: string) =>
  driveApi.post(`/items/${itemId}/tags`, { tag_id: tagId }).then(r => r.data);

export const detachTag = (itemId: string, tagId: string) =>
  driveApi.delete(`/items/${itemId}/tags/${tagId}`).then(r => r.data);

export const applyHold = (id: string, reason: string) =>
  driveApi.post(`/items/${id}/hold`, { reason }).then(r => r.data);

export const liftHold = (id: string) =>
  driveApi.delete(`/items/${id}/hold`).then(r => r.data);

export const setAvailability = (id: string, availability: 'online_only' | 'offline') =>
  driveApi.patch<DriveItem>(`/items/${id}/availability`, { availability }).then(r => r.data);

// ─── Versions ────────────────────────────────────────────────────────────────
export const fetchVersions = (itemId: string) =>
  driveApi.get<{ versions: FileVersion[] }>(`/items/${itemId}/versions`).then(r => r.data.versions);

export const restoreVersion = (itemId: string, versionId: string) =>
  driveApi.post(`/items/${itemId}/versions/${versionId}/restore`).then(r => r.data);

// ─── Comments ────────────────────────────────────────────────────────────────
export const fetchComments = (itemId: string) =>
  driveApi.get<{ comments: any[] }>(`/items/${itemId}/comments`).then(r =>
    r.data.comments.map((c: any) => ({ ...c, author_id: c.author_id ?? c.author_user_id })) as Comment[]
  );

export const addComment = (itemId: string, body: string) =>
  driveApi.post<Comment>(`/items/${itemId}/comments`, { body, mentions: [] }).then(r => r.data);

export const resolveComment = (itemId: string, commentId: string) =>
  driveApi.patch(`/items/${itemId}/comments/${commentId}`, { resolved: true }).then(r => r.data);

export const deleteComment = (itemId: string, commentId: string) =>
  driveApi.delete(`/items/${itemId}/comments/${commentId}`).then(r => r.data);

// ─── Sharing / Links ──────────────────────────────────────────────────────────
// Note: shares are their own resource — mounted at /shares, not nested under
// /items/:id — matching sk-drive-api's sharing router (target_type/grantee_type).
export const fetchShares = (itemId: string) =>
  driveApi.get<{ shares: ShareGrant[] }>('/shares', { params: { item_id: itemId } }).then(r => r.data.shares);

export const addShare = (itemId: string, userId: string, permission: SharePermission) =>
  driveApi.post<ShareGrant>('/shares', {
    target_type: 'item', item_id: itemId,
    grantee_type: 'user', grantee_id: userId,
    permission,
  }).then(r => r.data);

// Change an existing grant's tier (e.g. Viewer -> Full Share) in place, rather
// than removing and re-adding it.
export const updateSharePermission = (shareId: string, permission: SharePermission) =>
  driveApi.patch<ShareGrant>(`/shares/${shareId}`, { permission }).then(r => r.data);

export const removeShare = (shareId: string) =>
  driveApi.delete(`/shares/${shareId}`).then(r => r.data);

// ─── Permission requests ──────────────────────────────────────────────────────
// Ask the owner/manager to raise (or lower) your own tier on an item/folder.
export const requestPermission = (itemId: string, requestedPermission: SharePermission, reason?: string) =>
  driveApi.post<PermissionRequest>('/permission-requests', {
    target_type: 'item', item_id: itemId, requested_permission: requestedPermission,
    ...(reason ? { reason } : {}),
  }).then(r => r.data);

// The caller's own pending/past requests for one item — lets the requester's
// UI show "Pending" instead of offering to send another one.
export const fetchMyPermissionRequests = (itemId: string) =>
  driveApi.get<{ requests: PermissionRequest[] }>('/permission-requests/mine', { params: { item_id: itemId } }).then(r => r.data.requests);

// Incoming requests for an item — manager-only.
export const fetchIncomingPermissionRequests = (itemId: string) =>
  driveApi.get<{ requests: PermissionRequest[] }>('/permission-requests', { params: { item_id: itemId } }).then(r => r.data.requests);

export const decidePermissionRequest = (requestId: string, decision: 'approve' | 'deny') =>
  driveApi.post<PermissionRequest>(`/permission-requests/${requestId}/decision`, { decision }).then(r => r.data);

// Org directory for the "People & groups" picker — proxied through
// sk-drive-api → backbone → sk-people. Resolves to [] if the org doesn't
// license sk-people or the caller lacks directory access there.
export const fetchPeopleDirectory = () =>
  driveApi.get<{ people: Person[] }>('/people').then(r => r.data.people).catch(() => [] as Person[]);

// External share links on an item. An item can carry several at once, each
// separately named (e.g. "Client review", "Internal QA").
export const fetchLinks = (itemId: string) =>
  driveApi.get<{ links: SharedLink[] }>(`/items/${itemId}/links`).then(r => r.data.links);

// Request body fields the backend's createLinkSchema actually accepts —
// distinct from SharedLink (the response shape).
export interface CreateLinkOptions {
  name?: string;
  scope?: 'team' | 'anyone';
  permission?: SharePermission;
  allow_download?: boolean;
  allow_print?: boolean;
  password?: string;
  expires_at?: string;
}

export interface UpdateLinkOptions {
  // null clears the name back to "no name"; undefined leaves it untouched.
  name?: string | null;
  scope?: 'team' | 'anyone';
  permission?: SharePermission;
  allow_download?: boolean;
  allow_print?: boolean;
  // null clears an existing password / expiry; undefined leaves it untouched.
  password?: string | null;
  expires_at?: string | null;
}

export const createLink = (itemId: string, opts?: CreateLinkOptions) =>
  driveApi.post<SharedLink>(`/items/${itemId}/links`, opts ?? {}).then(r => r.data);

export const updateLink = (itemId: string, linkId: string, opts: UpdateLinkOptions) =>
  driveApi.patch<SharedLink>(`/items/${itemId}/links/${linkId}`, opts).then(r => r.data);

export const deleteLink = (itemId: string, linkId: string) =>
  driveApi.delete(`/items/${itemId}/links/${linkId}`).then(r => r.data);

export const fetchLinkStats = (itemId: string, linkId: string) =>
  driveApi.get<LinkStats>(`/items/${itemId}/links/${linkId}/stats`).then(r => r.data);

// ─── Upload ───────────────────────────────────────────────────────────────────
export const requestUpload = (spaceId: string, name: string, sizeBytes: number, mimeType: string, mode?: 'single' | 'multipart', partCount?: number, idempotencyKey?: string) =>
  driveApi.post('/items/uploads', {
    space_id: spaceId, name, size_bytes: sizeBytes, mime_type: mimeType,
    ...(mode ? { mode } : {}), ...(partCount ? { part_count: partCount } : {}),
    ...(idempotencyKey ? { idempotency_key: idempotencyKey } : {}),
  }).then(r => r.data);

export const completeUpload = (sessionId: string, parts?: { partNumber: number; etag: string }[]) =>
  driveApi.post<{ file_id: string; scan_status: string; state: string }>(
    `/files/uploads/${sessionId}/complete`, parts?.length ? { parts } : {}
  ).then(r => r.data);

export const abortUpload = (sessionId: string) =>
  driveApi.post(`/files/uploads/${sessionId}/abort`).then(() => undefined).catch(() => undefined);

// Re-presign a pending multipart session's part URLs and extend its expiry.
// Used for large/slow uploads that outlive the presigned-URL TTL (default 15m).
// Optionally scoped to just the part numbers still needing upload.
export const refreshUpload = (sessionId: string, partNumbers?: number[]) =>
  driveApi.post<{ session_id: string; mode: string; expires_at: string; part_urls?: { partNumber: number; url: string }[]; upload_url?: string }>(
    `/files/uploads/${sessionId}/refresh`, partNumbers?.length ? { part_numbers: partNumbers } : {}
  ).then(r => r.data);

// PUT raw bytes to a presigned storage URL (whole file or one multipart part),
// tracking progress and returning the response ETag (needed to complete a
// multipart upload). Uses XHR over fetch so upload progress is observable.
export function uploadBytes(url: string, blob: Blob, onProgress?: (loaded: number) => void, signal?: AbortSignal): Promise<string | null> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) { reject(new DOMException('Upload aborted', 'AbortError')); return; }
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', resolveStorageUrl(url));
    xhr.upload.onprogress = (e) => { if (onProgress && e.lengthComputable) onProgress(e.loaded); };
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300)
      ? resolve(xhr.getResponseHeader('ETag'))
      : reject(new Error(`Upload failed: ${xhr.status}`));
    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.onabort = () => reject(new DOMException('Upload aborted', 'AbortError'));
    if (signal) signal.addEventListener('abort', () => xhr.abort(), { once: true });
    xhr.send(blob);
  });
}

export const createItem = (spaceId: string, fileId: string, name: string, folderId?: string) =>
  driveApi.post<DriveItem>('/items', {
    space_id: spaceId, file_id: fileId, name,
    ...(folderId ? { folder_id: folderId } : {}),
  }).then(r => r.data);

// ─── Download / Content ──────────────────────────────────────────────────────
export const getContentUrl = (itemId: string, download = false) =>
  driveApi.get<{ url: string; expires_in: number }>(
    `/items/${itemId}/content`, { params: download ? { download: 'true' } : {} }
  ).then(r => resolveStorageUrl(r.data.url));

export const getDownloadUrl = (itemId: string) => getContentUrl(itemId, true);

// Why a document has no preview. 'unsupported' is terminal; the rest may resolve
// on their own, so the UI can offer a retry instead of a dead end.
export type PreviewUnavailableReason = 'unsupported' | 'scanning' | 'pending' | 'failed' | 'unavailable';

export interface PreviewTarget {
  url: string;
  /** 'native' = the file is already a PDF; 'derived' = rendered from Office. */
  kind: 'native' | 'derived';
  mime_type: string;
}

/**
 * Inline-renderable URL for a document. Resolves for PDFs and for Office files
 * the pipeline has converted; rejects with a `reason` otherwise.
 */
export const getPreviewTarget = (itemId: string): Promise<PreviewTarget> =>
  driveApi
    .get<PreviewTarget>(`/items/${itemId}/preview`)
    .then(r => ({ ...r.data, url: resolveStorageUrl(r.data.url) }))
    .catch((err: DriveApiError) => {
      const reason = (err.details as { reason?: PreviewUnavailableReason } | undefined)?.reason ?? 'unsupported';
      return Promise.reject(Object.assign(new Error(err.message || 'No preview'), { reason }));
    });

// Plain-text content for the text viewer. Capped so a multi-gigabyte log can't
// be pulled into memory; the caller shows a truncation notice.
export const TEXT_PREVIEW_LIMIT = 2 * 1024 * 1024;

export async function fetchTextContent(itemId: string): Promise<{ text: string; truncated: boolean }> {
  const url = await getContentUrl(itemId);
  const resp = await fetch(url);
  if (!resp.ok) throw new Error(`Could not load file (${resp.status})`);
  const blob = await resp.blob();
  const truncated = blob.size > TEXT_PREVIEW_LIMIT;
  const text = await (truncated ? blob.slice(0, TEXT_PREVIEW_LIMIT) : blob).text();
  return { text, truncated };
}

// Signed URL for the item's 320px derived thumbnail (image/video). 404s when none
// exists yet — callers should treat a rejection as "no thumbnail, use the glyph".
export const getThumbnailUrl = (itemId: string) =>
  driveApi.get<{ url: string; expires_in: number }>(`/items/${itemId}/thumbnail`)
    .then(r => resolveStorageUrl(r.data.url));

// Blob-fetch download — works cross-origin (frontend :3011 → drive api :3002).
export async function downloadFile(itemId: string, filename: string): Promise<void> {
  const url = await getDownloadUrl(itemId);
  const resp = await fetch(url);
  const blob = await resp.blob();
  const obj = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = obj; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(obj), 1000);
}

// ─── Transfers ───────────────────────────────────────────────────────────────
export const fetchTransfers = () =>
  driveApi.get<{ transfers: Transfer[] }>('/transfers').then(r => r.data.transfers);

// The API takes an absolute expiry and resolves item ids to their current
// version's Core blob server-side, so the picker only deals in Drive items.
export const createTransfer = (
  name: string,
  itemIds: string[],
  expiresAt: string,
  password?: string,
) =>
  driveApi
    .post<Transfer>('/transfers', {
      name,
      item_ids: itemIds,
      expires_at: expiresAt,
      ...(password ? { password } : {}),
    })
    .then(r => r.data);

export const disableTransfer = (id: string) =>
  driveApi.post<{ id: string; state: Transfer['state'] }>(`/transfers/${id}/disable`).then(r => r.data);

export const deleteTransfer = (id: string) =>
  driveApi.delete(`/transfers/${id}`).then(r => r.data);

// ─── File Requests ───────────────────────────────────────────────────────────
export const fetchFileRequests = () =>
  driveApi.get<{ requests: FileRequest[] }>('/file-requests').then(r => r.data.requests);

export const createFileRequest = (title: string, spaceId: string, folderId?: string, deadline?: string) =>
  driveApi.post<FileRequest>('/file-requests', { title, space_id: spaceId, ...(folderId ? { folder_id: folderId } : {}), ...(deadline ? { deadline } : {}) }).then(r => r.data);

export const closeFileRequest = (id: string) =>
  driveApi.post<FileRequest>(`/file-requests/${id}/close`).then(r => r.data);

export const reopenFileRequest = (id: string) =>
  driveApi.post<FileRequest>(`/file-requests/${id}/reopen`).then(r => r.data);

export const deleteFileRequest = (id: string) =>
  driveApi.delete(`/file-requests/${id}`).then(r => r.data);

// ─── Members ─────────────────────────────────────────────────────────────────
export const fetchMembers = () =>
  driveApi.get<{ members: WorkspaceMember[] }>('/members').then(r => r.data.members);

export const inviteMember = (userId: string, role: string, quotaBytes: number) =>
  driveApi.post<WorkspaceMember>('/members', { user_id: userId, role, quota_bytes: quotaBytes }).then(r => r.data);

export const updateMember = (id: string, data: Partial<Pick<WorkspaceMember, 'role' | 'status'> & { quota_bytes: number }>) =>
  driveApi.patch<WorkspaceMember>(`/members/${id}`, data).then(r => r.data);

export const removeMember = (id: string) =>
  driveApi.delete(`/members/${id}`).then(r => r.data);

// ─── Storage Requests ────────────────────────────────────────────────────────
export const fetchStorageRequests = (status?: string) =>
  driveApi.get<{ requests: StorageRequest[] }>('/storage-requests', { params: status ? { status } : {} }).then(r => r.data.requests);

export const approveStorageRequest = (id: string) =>
  driveApi.post(`/storage-requests/${id}/approve`).then(r => r.data);

export const denyStorageRequest = (id: string) =>
  driveApi.post(`/storage-requests/${id}/deny`).then(r => r.data);

export const requestStorage = (amountBytes: number, reason: string) =>
  driveApi.post('/storage-requests', { amount_bytes: amountBytes, reason }).then(r => r.data);

// ─── Usage / Analytics ───────────────────────────────────────────────────────
export const fetchUsage = () =>
  driveApi.get<UsageSummary>('/usage').then(r => r.data);

export const fetchAnalytics = () =>
  driveApi.get('/analytics').then(r => r.data);

// ─── Tags ────────────────────────────────────────────────────────────────────
export const fetchTags = () =>
  driveApi.get<{ tags: Tag[] }>('/tags').then(r => r.data.tags);

export const createTag = (name: string, color?: string) =>
  driveApi.post<Tag>('/tags', { name, ...(color ? { color } : {}) }).then(r => r.data);

// ─── Notifications ───────────────────────────────────────────────────────────
export const fetchNotifications = () =>
  driveApi.get<{ notifications: Notification[] }>('/notifications').then(r => r.data.notifications ?? []).catch(() => [] as Notification[]);

export const markNotificationsRead = () =>
  driveApi.post('/notifications/read').catch(() => {});

// ─── Billing ─────────────────────────────────────────────────────────────────
export const fetchBilling = () =>
  driveApi.get('/billing').then(r => r.data);
